import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Terminal, LogOut, User, ChevronDown, MoreHorizontal,
} from 'lucide-react';
import { auth } from '@/lib/api';
import { visibleItems } from './navigation.config';
import useActiveRoute from './useActiveRoute';
import useViewportWidth from '@/hooks/useViewportWidth';
import useReadinessSignal from '@/hooks/useReadinessSignal';

const LEVEL_COLOR = {
  green: 'var(--accent-green, #00C853)',
  amber: 'var(--accent-amber, #FFB000)',
  red:   'var(--accent-red, #FF3B30)',
};

function ReadinessDot({ level, pct, size = 8, title }) {
  if (!level || level === 'hidden') return null;
  return (
    <span data-testid="nav-readiness-dot"
          data-level={level}
          title={title || `Readiness ${pct}%`}
          style={{
            display: 'inline-block',
            width: size, height: size,
            borderRadius: '50%',
            background: LEVEL_COLOR[level] || LEVEL_COLOR.red,
            boxShadow: `0 0 6px ${LEVEL_COLOR[level]
              || LEVEL_COLOR.red}`,
            marginLeft: 6,
            animation: level === 'green'
              ? 'apex-pulse 1.4s ease-in-out infinite' : 'none',
          }} />
  );
}

/**
 * DesktopNavbar — sticky top SaaS navbar for >=768px viewports.
 *
 * Layout:
 *   [Logo] [primary nav left]  ............... [User chip] [MORE] [Logout]
 *
 * Features:
 *   • Sticky (position: sticky; top: 0; zIndex: 100).
 *   • Responsive demotion: when nav items don't fit, the rightmost-listed
 *     primary items get pushed into the MORE dropdown.
 *   • Active state: amber underline + accent color on current route.
 *   • Role-aware visibility via navigation.config.js.
 *   • A11y: aria-haspopup/expanded on MORE; aria-current on active link.
 */
export default function DesktopNavbar() {
  const navigate = useNavigate();
  const isActive = useActiveRoute();
  const user = auth.currentUser();
  const role = user?.role || 'operator';
  const vpw = useViewportWidth();

  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const readiness = useReadinessSignal();

  // Close on outside click + Escape
  useEffect(() => {
    if (!moreOpen) return;
    const onClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setMoreOpen(false); };
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);

  const primaryAll = visibleItems(role, 'desktop-primary');
  const moreItems  = visibleItems(role, 'more');

  // Responsive demotion thresholds — move rightmost primary items into
  // MORE when the viewport gets narrow.  Tablet upgrade 2026-02-11:
  // 900–1100 (tablet landscape) gets a tighter ladder so the brand,
  // logout, user chip, MORE button, and 3 primary items all fit
  // without horizontal overflow.
  let visibleCount = primaryAll.length;
  if (vpw < 1280) visibleCount = Math.min(visibleCount, 6);
  if (vpw < 1180) visibleCount = Math.min(visibleCount, 5);
  if (vpw < 1024) visibleCount = Math.min(visibleCount, 4);
  if (vpw < 940)  visibleCount = Math.min(visibleCount, 3);
  if (vpw < 900)  visibleCount = Math.min(visibleCount, 2);
  const visiblePrimary = primaryAll.slice(0, visibleCount);
  const demotedPrimary = primaryAll.slice(visibleCount);

  const moreCombined = [...demotedPrimary, ...moreItems];

  const logout = () => { auth.logout(); navigate('/login'); };

  return (
    <header
      data-testid="apex-desktop-navbar"
      style={{
        position: 'sticky', top: 0, zIndex: 200,
        background: '#0f0f0f',
        borderBottom: '1px solid var(--border)',
      }}>
      <div className="flex items-center justify-between gap-3 px-4 py-2"
           style={{ minHeight: 48, flexWrap: 'nowrap' }}>
        {/* Brand */}
        <div className="flex items-center gap-3"
             style={{ minWidth: 0, flex: '0 0 auto' }}>
          <Link to="/" className="flex items-center gap-2"
                data-testid="nav-brand"
                style={{ textDecoration: 'none',
                         color: 'var(--text-primary)',
                         whiteSpace: 'nowrap' }}>
            <Terminal style={{ color: 'var(--accent-amber)' }} size={18} />
            <span className="font-sans font-bold tracking-tight"
                  style={{ fontSize: 16 }}>APEXFLOW</span>
            <span className="label-xs hidden lg:inline"
                  style={{ color: 'var(--accent-amber)' }}>GOD MODE</span>
          </Link>
        </div>

        {/* Primary nav */}
        <nav data-testid="nav-primary"
             aria-label="Primary"
             className="flex items-center gap-1"
             style={{ flex: '1 1 auto', minWidth: 0,
                      justifyContent: 'flex-start',
                      marginLeft: 12, flexWrap: 'nowrap',
                      overflow: 'hidden' }}>
          {visiblePrimary.map((it) => {
            const active = isActive(it.to);
            return (
              <Link key={it.id} to={it.to}
                    data-testid={it.testId}
                    data-active={active}
                    aria-current={active ? 'page' : undefined}
                    className="btn-sharp"
                    style={{
                      whiteSpace: 'nowrap',
                      color: active
                        ? 'var(--accent-amber)' : 'var(--text-primary)',
                      borderBottom: active
                        ? '2px solid var(--accent-amber)'
                        : '2px solid transparent',
                    }}>
                <it.Icon size={12}
                         style={{ display: 'inline', marginRight: 4 }} />
                {it.label}
              </Link>
            );
          })}
        </nav>

        {/* Right cluster: user · MORE · logout */}
        <div className="flex items-center gap-2"
             style={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}>
          {vpw >= 1024 && user && (
            <div className="flex items-center gap-2 label-xs"
                 data-testid="nav-user-chip"
                 style={{ whiteSpace: 'nowrap' }}>
              <User size={12} />
              <span style={{ maxWidth: 160, overflow: 'hidden',
                             textOverflow: 'ellipsis' }}>{user.email}</span>
              <span style={{ color: role === 'admin'
                ? 'var(--accent-amber)' : 'var(--text-secondary)' }}>
                · {role.toUpperCase()}
              </span>
            </div>
          )}

          {moreCombined.length > 0 && (
            <div ref={moreRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setMoreOpen((o) => !o)}
                data-testid="nav-overflow-toggle"
                aria-haspopup="menu"
                aria-expanded={moreOpen}
                aria-label="More navigation"
                className="btn-sharp"
                style={{ display: 'inline-flex', alignItems: 'center',
                         gap: 4, whiteSpace: 'nowrap',
                         position: 'relative' }}>
                <MoreHorizontal size={12} />
                MORE
                <ChevronDown size={10} />
                {readiness.level !== 'hidden' && (
                  <span style={{ position: 'absolute',
                                 top: 2, right: 2 }}>
                    <ReadinessDot
                      level={readiness.level}
                      pct={readiness.readiness_pct}
                      size={7}
                      title={`Promotion readiness `
                        + `${readiness.readiness_pct}%`} />
                  </span>
                )}
              </button>
              {moreOpen && (
                <div data-testid="nav-overflow-dropdown"
                     role="menu"
                     style={{
                       position: 'absolute', right: 0,
                       top: 'calc(100% + 4px)',
                       minWidth: 240, zIndex: 9000,
                       background: '#0a0a0a',
                       border: '1px solid var(--border)',
                       boxShadow: '0 4px 16px rgba(0,0,0,0.6)',
                       maxHeight: '70vh', overflowY: 'auto',
                     }}>
                  {moreCombined.map((it) => {
                    const active = isActive(it.to);
                    return (
                      <Link key={it.id} to={it.to}
                            data-testid={`nav-overflow-${it.id}`}
                            role="menuitem"
                            onClick={() => setMoreOpen(false)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '10px 12px', fontSize: 11,
                              letterSpacing: '0.08em',
                              color: active
                                ? 'var(--accent-amber)'
                                : 'var(--text-primary)',
                              textDecoration: 'none',
                              borderBottom: '1px solid var(--border)',
                              background: active ? '#1a1a1a' : 'transparent',
                            }}
                            onMouseEnter={(e) =>
                              e.currentTarget.style.background = '#1a1a1a'}
                            onMouseLeave={(e) =>
                              e.currentTarget.style.background = active
                                ? '#1a1a1a' : 'transparent'}>
                        <it.Icon size={12}
                                 style={{ color: 'var(--accent-amber)' }} />
                        {it.label}
                        {it.id === 'promotion-console'
                          && readiness.level !== 'hidden' && (
                          <ReadinessDot
                            level={readiness.level}
                            pct={readiness.readiness_pct}
                            title={`${readiness.readiness_pct}% ready`}
                          />
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <button className="btn-sharp" onClick={logout}
                  data-testid="logout-btn"
                  aria-label="Log out"
                  style={{ whiteSpace: 'nowrap' }}>
            <LogOut size={12}
                    style={{ display: 'inline', marginRight: 4 }} />
            LOGOUT
          </button>
        </div>
      </div>
    </header>
  );
}
